import { useEffect, useState } from "react";

import {
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  SearchBar,
  buildPath,
  buildTemplatePath,
  pn,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
} from "@compass/core";

import { ListFooter, ReorderDataModels } from "components";
import { GradeListDto, GradeListResource, ListFilterBase, Permission, DataModelEnum, PermissionAction } from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { isAuthorized } from "services";
import { GradeListPageHeader } from "./GradeListPageHeader";
import { GradeTable } from "./GradeTable";

const routeName = "grades";

export const gradeListPageRoute: PageRoute = {
  handle: "Course Grades",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const GradeListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.MatchUtility;

  const [listItems, setListItems] = useState<GradeListDto[]>([]);
  const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
    sortObjects: [{ property: pn<GradeListDto>("name"), isDesc: false }],
  });
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<GradeListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.grades.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.grades.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);

    ApiService.grades.getListResource().then((response) => {
      setResource(response);
    });
  }, []);

  return (
    <>
      <Page header={<GradeListPageHeader rootPermission={ROOT_PERMISSION} handle={gradeListPageRoute.handle} />}>
        <ListContainer
          fullHeight
          header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
          tableContainer={
            <GradeTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              grades={listItems}
              setGrades={setListItems}
              resource={resource}
              searchFocused={searchFocused}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
              reorderDataModels={
                  isAuthorized(ROOT_PERMISSION, PermissionAction.Update) ? (
                    <ReorderDataModels
                      dataModelEnum={DataModelEnum.Grade}
                      handle={gradeListPageRoute.handle}
                      getId={(item) => item.id}
                      getName={(item) => item.name}
                      getDisplayOrder={(item) => item.displayOrder}
                      fetch={() =>
                        ApiService.grades.getList({
                          page: PAGE_DEFAULT,
                          pageSize: PAGE_SIZE_UNLIMITED,
                        })
                      }
                    />
                  ) : undefined
                }
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
